/**
 * Trees API Client
 *
 * @remarks
 * Cliente HTTP para interactuar con los endpoints del módulo Trees/Environmental.
 * Maneja autenticación automáticamente usando el token del store.
 *
 * FASE B: API mínima para cargar árboles reales en el mapa.
 */

import axios, { type AxiosInstance } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

export interface Tree {
  id: string;
  code: string;
  projectId: string;
  coordinates: GeoJSON.Point;
  data: {
    commonName?: string;
    scientificName?: string;
    generalCondition?: string;
    height?: number;
    trunkDiameter?: number;
    [key: string]: any;
  };
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Crea una instancia de axios con configuración base
 */
function createApiInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: `${API_URL}/trees`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Interceptor para agregar token automáticamente
  instance.interceptors.request.use(
    (config) => {
      const authStore = localStorage.getItem('auth-storage');
      if (authStore) {
        try {
          const parsed = JSON.parse(authStore);
          const token = parsed.state?.token;
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('[TreesAPI] Error parsing auth token:', error);
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  return instance;
}

const api = createApiInstance();

/**
 * Cliente API para gestión de árboles
 */
export const treesApi = {
  /**
   * Obtiene todos los árboles de un proyecto
   */
  async getByProject(projectId: string): Promise<Tree[]> {
    const response = await api.get<Tree[]>(`/project/${projectId}`);
    return response.data;
  },

  /**
   * Convierte árboles a GeoJSON FeatureCollection
   */
  toGeoJSON(trees: Tree[]): GeoJSON.FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: trees.map((tree) => ({
        type: 'Feature',
        id: tree.id,
        geometry: tree.coordinates,
        properties: {
          id: tree.id,
          code: tree.code,
          commonName: tree.data?.commonName,
          scientificName: tree.data?.scientificName,
          condition: tree.data?.generalCondition,
          height: tree.data?.height,
          diameter: tree.data?.trunkDiameter,
        },
      })),
    };
  },
};
